import * as React from 'react';
import { useTranslation } from 'react-i18next';

import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { UniversalCombobox } from '@/components/common/UniversalCombobox';
import { useTeamMutations } from '../hooks';
import type { Matter } from '../types';

interface ReassignStaffDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  matter: Matter | null;
  /** The outgoing Contributor (the row the action was clicked on). */
  fromUser: { id: number; name?: string | null | undefined } | null;
  onSuccess?: () => void;
}

// Case 360° (Stage 14) — replace a Contributor: their active tasks transfer to
// the incoming staffer (prior work preserved as finished 0% adjustments) and
// their running timers are stopped automatically. All server-enforced.
export function ReassignStaffDialog({
  open,
  onOpenChange,
  matter,
  fromUser,
  onSuccess,
}: ReassignStaffDialogProps) {
  const { t } = useTranslation();
  const [toUserId, setToUserId] = React.useState<number | undefined>(undefined);
  const { reassignStaffMutation } = useTeamMutations();

  React.useEffect(() => {
    if (open) setToUserId(undefined);
  }, [open]);

  const handleConfirm = async () => {
    if (!matter || !fromUser || !toUserId) return;
    await reassignStaffMutation.mutateAsync({
      id: matter.id,
      fromUserId: fromUser.id,
      toUserId,
    });
    onOpenChange(false);
    onSuccess?.();
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {t('matterTeam.reassignStaffTitle', { name: fromUser?.name ?? '' })}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {t('matterTeam.reassignStaffDescription')}
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="my-4">
          <label className="mb-2 block text-sm font-medium">
            {t('matterTeam.incomingStaff')}
          </label>
          <UniversalCombobox
            url="/users"
            listUrl="/users/select-box"
            labelKey="name"
            valueKey="id"
            selectedId={toUserId}
            params={{ status: 'ACTIVE' }}
            onSelectItem={(item) =>
              setToUserId(item?.['id'] as number | undefined)
            }
            placeholder={t('matterTeam.selectUser')}
          />
          <p className="mt-1 text-xs text-slate-400">
            {t('matterTeam.addMemberHint')}
          </p>
        </div>

        <AlertDialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={reassignStaffMutation.isPending}
          >
            {t('cancel')}
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={!toUserId || reassignStaffMutation.isPending}
          >
            {reassignStaffMutation.isPending
              ? t('processing')
              : t('matterTeam.reassignStaffConfirm')}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
